import { redirect } from '@tanstack/react-router';
import { TenantContext } from '@interxfi/api-client';
import { useRealmAuthStore as realmAuthStore } from '@interxfi/api-client/context';
import type { WhoAmIContext } from '@interxfi/api-client/realm-auth/store';
import type { IxmUserauthV1JoinedRealmAccess } from '@interxfi/types';
import { sortByPriority } from '@interxfi/utilities/sort';
import { DEFAULT_AUTHENTICATED_ROUTE } from './defaults';

const MAX_WAIT = 15_000;
const ACCESS_CHECK_INTERVAL = 50;

export interface RouteGuardAuthenticationOptions {
  redirectToIfMfaRequirementNotMet?: string;
  redirectToIfNotMet: string;
  requireAuthenticated: boolean;
}

export async function routeGuardRequireAuthentication(options: RouteGuardAuthenticationOptions, redirectSearch?: Record<string, string>) {
  function checkFailsMfaRequirement() {
    const state = realmAuthStore.getState();

    if (state.context?.organizationMemberships?.find((membership) => membership.organization?.data?.controls?.settings?.requireMfa)) {
      return !state.context?.hasMFA;
    }

    return false;
  }

  function checkState() {
    const state = realmAuthStore.getState();

    if (state.isAuthenticating) {
      return 'pending';
    }

    if (options.requireAuthenticated) {
      return state.isAuthenticated ? 'success' : 'fail';
    }

    return state.isAuthenticated ? 'fail' : 'success';
  }

  if (checkState() === 'success') {
    return;
  }

  return new Promise((resolve, reject) => {
    let runTime = 0;

    function rejectAndRedirect() {
      reject(redirect({ to: options.redirectToIfNotMet, search: redirectSearch }));
    }

    const i = window.setInterval(() => {
      runTime += ACCESS_CHECK_INTERVAL;

      const status = checkState();

      switch (status) {
        case 'success': {
          clearInterval(i);

          if (options.redirectToIfMfaRequirementNotMet) {
            if (checkFailsMfaRequirement()) {
              reject(redirect({ to: options.redirectToIfMfaRequirementNotMet, search: redirectSearch }));
              return;
            }
          }

          resolve(true);
          return;
        }
        case 'fail':
          clearInterval(i);
          rejectAndRedirect();
          return;
        case 'pending':
          if (runTime >= MAX_WAIT) {
            clearInterval(i);
            rejectAndRedirect();
          }
      }
    }, ACCESS_CHECK_INTERVAL);
  });
}

export interface RouteGuardValidRealmAccessOptions {
  allowedTenantTypes: TenantContext[];
}

export interface RouteGuardOptions {
  guard: {
    access?: RouteGuardValidRealmAccessOptions;
    auth?: RouteGuardAuthenticationOptions;
  };
}

export type RouteGuardOptionsWithAdditionalRedirectContext = RouteGuardOptions & {
  additionalRedirectContext?: Record<string, string>;
};

interface RouteGuardValidRealmAccessCheckOutput {
  match?: IxmUserauthV1JoinedRealmAccess;
  prospects: IxmUserauthV1JoinedRealmAccess[];
}

export function checkAccess(
  options: RouteGuardValidRealmAccessOptions,
  activeAccess: IxmUserauthV1JoinedRealmAccess | undefined,
  context: WhoAmIContext | undefined,
) {
  const output: RouteGuardValidRealmAccessCheckOutput = { prospects: [] };

  if (!activeAccess || !context) {
    return output;
  }

  const realmAccessesToCheck = sortByPriority(
    context.accesses.filter(
      (access) => access.tenant?.tenantType && (options.allowedTenantTypes || []).includes(access.tenant.tenantType as TenantContext),
    ),
    [TenantContext.Clearing, TenantContext.Program, TenantContext.Platform, TenantContext.Organization, TenantContext.O5],
    (access) => access?.tenant?.tenantType || '',
  );

  for (const access of realmAccessesToCheck) {
    if (access.access?.realmAccessId) {
      if (!output.match && access.access.realmAccessId === activeAccess.access?.realmAccessId) {
        output.match = access;
      } else {
        output.prospects.push(access);
      }
    }
  }

  return output;
}

export async function routeGuardValidRealmAccess(options: RouteGuardValidRealmAccessOptions): Promise<IxmUserauthV1JoinedRealmAccess> {
  function checkState() {
    const { isAuthenticated, isAuthenticating, activeAccess, context } = realmAuthStore.getState();

    if (isAuthenticating) {
      return { status: 'pending' } as const;
    }

    if (!isAuthenticated) {
      return { status: 'fail' } as const;
    }

    const outcome = checkAccess(options, activeAccess, context);
    if (outcome.match || outcome.prospects?.length) {
      return { status: 'success', outcome } as const;
    }

    return { status: 'fail' } as const;
  }

  // In the initial check, if the current access is already a match, return immediately and don't update the active access
  const match = checkState().outcome?.match;
  if (match) {
    return match;
  }

  return new Promise((resolve, reject) => {
    let runTime = 0;

    function rejectAndRedirect() {
      reject(redirect({ to: DEFAULT_AUTHENTICATED_ROUTE }));
    }

    const i = window.setInterval(() => {
      runTime += ACCESS_CHECK_INTERVAL;

      const currentState = checkState();
      switch (currentState.status) {
        case 'fail':
          window.clearInterval(i);
          rejectAndRedirect();
          return;
        case 'success': {
          window.clearInterval(i);

          const changeTo = currentState.outcome?.match || currentState.outcome?.prospects[0];
          if (changeTo?.access?.realmAccessId) {
            const { setActiveAccess } = realmAuthStore.getState();
            setActiveAccess(changeTo.access.realmAccessId);
          }

          resolve(changeTo);
          return;
        }
        case 'pending':
          if (runTime >= MAX_WAIT) {
            window.clearInterval(i);
            rejectAndRedirect();
          }

          return;
      }
    }, ACCESS_CHECK_INTERVAL);
  });
}
